import { botApi, type BacktestEventRaw } from '../api'
import type { FillEffect, OrderData, OrderEventData, PendingOrderType } from '../types/chartData'
import { toApiTimestamp } from '../utils/timeParsing'

const MAX_CACHE_ENTRIES = 50

export interface BacktestActivity {
  /** Position opens and closes: what every chart draws as trades. */
  orders: OrderData[]
  /** The walkthrough's pending-order and netting events, in event order. */
  activity: OrderEventData[]
}

const cache = new Map<string, BacktestActivity>()

function cacheKey(btid: string, startTime: string, endTime: string): string {
  return `${btid}:${startTime}:${endTime}`
}

function cacheSet(key: string, value: BacktestActivity): void {
  if (cache.has(key)) cache.delete(key)
  cache.set(key, value)
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
    else break
  }
}

/**
 * Engine time strings to unix seconds.
 *
 * The engine writes `YYYY-MM-DD HH:MM:SS+00:00` (sometimes with microseconds); the events table
 * returns `YYYY-MM-DD HH:MM:SS.mmm`; older code appended `Z` to everything, which produced
 * `…+00:00Z` and relied on V8 forgiving it. A naive string is UTC; a zoned one is parsed as is;
 * fractional seconds are cut to milliseconds, the most Date accepts.
 */
export function toUnixSeconds(dateStr: string): number {
  const s = dateStr.trim().replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1')
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/.test(s)
  return Math.floor(new Date(zoned ? s : `${s}Z`).getTime() / 1000)
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

const ORDER_TYPES: ReadonlySet<string> = new Set(['buy_limit', 'sell_limit', 'buy_stop', 'sell_stop'])
const orderType = (v: unknown): PendingOrderType | null =>
  typeof v === 'string' && ORDER_TYPES.has(v) ? (v as PendingOrderType) : null
const sideOf = (v: unknown): 'BUY' | 'SELL' | null => (v === 'BUY' || v === 'SELL' ? v : null)

export function transformEvent(e: BacktestEventRaw): OrderData | null {
  if (!e.payload) return null
  const p = e.payload
  const ticket = num(p.ticket) ?? undefined

  if (e.event_type === 'position_opened') {
    if (!p.entry_time || !p.type || p.entry_price == null) return null
    return {
      orderId: e.event_id,
      time: toUnixSeconds(p.entry_time),
      type: p.type as 'BUY' | 'SELL',
      price: p.entry_price,
      sl: p.sl ?? 0,
      tp: p.tp ?? 0,
      eventType: 'opened',
      volume: p.volume,
      ...(ticket != null ? { ticket } : {}),
      ...(p.source === 'market' || p.source === 'pending' ? { source: p.source } : {}),
      ...(p.order_ticket !== undefined ? { orderTicket: p.order_ticket ?? null } : {}),
    }
  }

  if (e.event_type === 'position_closed') {
    if (!p.exit_time || !p.type || p.exit_price == null) return null
    return {
      orderId: e.event_id,
      time: toUnixSeconds(p.exit_time),
      type: p.type as 'BUY' | 'SELL',
      price: p.exit_price,
      sl: p.sl ?? 0,
      tp: p.tp ?? 0,
      eventType: 'closed',
      volume: p.volume,
      entryTime: p.entry_time ? toUnixSeconds(p.entry_time) : undefined,
      entryPrice: p.entry_price,
      exitPrice: p.exit_price,
      pnl: p.pnl,
      exitReason: p.exit_reason,
      balance: p.balance,
      commissionCost: p.commission_cost,
      ...(ticket != null ? { ticket } : {}),
      ...(Array.isArray(p.partial_exits)
        ? {
            partialExits: p.partial_exits.map((x) => ({
              time: toUnixSeconds(x.time),
              volume: x.volume,
              price: x.price,
              pnl: x.pnl,
              commissionCost: x.commission_cost,
              exitReason: x.exit_reason,
            })),
          }
        : {}),
      ...(num(p.avg_exit_price) != null ? { avgExitPrice: p.avg_exit_price } : {}),
      ...(num(p.partial_exits_count) != null ? { partialExitCount: p.partial_exits_count as number } : {}),
    }
  }

  return null
}

/**
 * The nine activity types: the pending-order and netting kinds of engine revision 4, the stop
 * moves and refused actions of engine API 3. Anything else — a `stop_out`, a row missing its
 * time — is dropped.
 */
export function transformActivity(e: BacktestEventRaw): OrderEventData | null {
  const p = e.payload
  if (!p || !p.time) return null
  const base = { eventId: e.event_id, time: toUnixSeconds(p.time), ticket: num(p.ticket) ?? 0 }
  const side = sideOf(p.side)
  const type = orderType(p.type)
  switch (e.event_type) {
    case 'order_placed':
      if (!type || !side || num(p.price) == null) return null
      return {
        ...base, kind: 'order_placed', orderId: num(p.order_id) ?? 0, orderType: type, side,
        price: p.price as number, volume: num(p.volume) ?? 0, sl: num(p.sl) || null, tp: num(p.tp) || null,
        expiryBars: num(p.expiry_bars), expiresAt: p.expires_at ? toUnixSeconds(p.expires_at) : null,
      }
    case 'order_filled':
      if (!type || !side || num(p.fill_price) == null) return null
      return {
        ...base, kind: 'order_filled', orderId: num(p.order_id) ?? 0, orderType: type, side,
        price: num(p.price) ?? (p.fill_price as number), fillPrice: p.fill_price as number,
        fillVolume: num(p.fill_volume) ?? num(p.volume) ?? 0, positionTicket: num(p.position_ticket),
        effect: (['open', 'increase', 'reduce', 'close', 'flip'].includes(String(p.effect))
          ? p.effect
          : 'open') as FillEffect,
      }
    case 'order_cancelled':
      if (!type || !side) return null
      return {
        ...base, kind: 'order_cancelled', orderId: num(p.order_id) ?? 0, orderType: type, side,
        price: num(p.price) ?? 0, volume: num(p.volume) ?? 0, reason: p.reason ?? 'cancelled',
        detail: p.detail ?? '',
      }
    case 'order_expired':
      if (!type || !side) return null
      return {
        ...base, kind: 'order_expired', orderId: num(p.order_id) ?? 0, orderType: type, side,
        price: num(p.price) ?? 0, volume: num(p.volume) ?? 0,
      }
    case 'order_rejected':
      return {
        ...base, kind: 'order_rejected', orderId: num(p.order_id) ?? 0, orderType: type, side,
        price: num(p.price), volume: num(p.volume), reason: p.reason ?? 'rejected',
        rejectKind: p.kind === 'cancel' ? 'cancel' : 'place',
      }
    case 'position_increased':
      if (!side || num(p.entry_price) == null) return null
      return {
        ...base, kind: 'position_increased', orderTicket: num(p.order_ticket), side,
        fillPrice: num(p.fill_price) ?? 0, fillVolume: num(p.fill_volume) ?? num(p.added_volume) ?? 0,
        volume: num(p.volume) ?? 0, entryPrice: p.entry_price as number, sl: num(p.sl) || null,
        tp: num(p.tp) || null,
      }
    case 'position_reduced':
      if (!side || num(p.exit_price) == null) return null
      return {
        ...base, kind: 'position_reduced', orderTicket: num(p.order_ticket), side,
        closedVolume: num(p.closed_volume) ?? 0, remainingVolume: num(p.remaining_volume) ?? 0,
        exitPrice: p.exit_price as number, pnl: num(p.pnl) ?? 0, commissionCost: num(p.commission_cost) ?? 0,
        exitReason: p.exit_reason ?? 'Order Fill', balance: num(p.balance) ?? 0,
      }
    case 'position_modified':
      if (!side) return null
      return {
        ...base, kind: 'position_modified', side, sl: num(p.sl) || null, tp: num(p.tp) || null,
        previousSl: num(p.previous_sl) || null, previousTp: num(p.previous_tp) || null,
      }
    case 'action_rejected':
      // The engine names the position a close / modify refusal is about in `id`
      // (a refused open names none): that is the row's ticket.
      return {
        ...base, ticket: num(p.id) ?? num(p.ticket) ?? 0,
        kind: 'action_rejected', action: typeof p.action === 'string' ? p.action : 'action', side,
        volume: num(p.volume), reason: p.reason ?? 'rejected',
      }
    default:
      return null
  }
}

/**
 * Every event of a run in a window, split into the position events every chart draws and the
 * walkthrough's activity stream. One fetch, one cache entry, so the trades pane, the canvas and
 * the Backtest tab read the same rows.
 */
export async function fetchBacktestActivity(
  btid: string,
  startTs: number,
  endTs: number,
): Promise<BacktestActivity> {
  const startTime = toApiTimestamp(startTs)
  const endTime = toApiTimestamp(endTs)
  const key = cacheKey(btid, startTime, endTime)

  const cached = cache.get(key)
  if (cached) return cached

  try {
    const events = await botApi.getBacktestEventsWindow(btid, startTime, endTime)
    const orders = events.map(transformEvent).filter((o): o is OrderData => o !== null)
    orders.sort((a, b) => a.time - b.time)
    const activity = events.map(transformActivity).filter((a): a is OrderEventData => a !== null)
    activity.sort((a, b) => a.time - b.time || Number(a.eventId) - Number(b.eventId))
    const result = { orders, activity }
    cacheSet(key, result)
    return result
  } catch {
    return { orders: [], activity: [] }
  }
}

/** The position events only: what the Backtest tab and the chart windows draw. */
export async function fetchBacktestEvents(
  btid: string,
  startTs: number,
  endTs: number,
): Promise<OrderData[]> {
  return (await fetchBacktestActivity(btid, startTs, endTs)).orders
}

export function clearEventCache(): void {
  cache.clear()
}
